/**
 * Classic checkout: shipping-first address inversion (front-end behaviour).
 *
 * Companion to woocommerce/checkout/form-checkout.php and
 * includes/checkout-billing-inversion.php.
 *
 * - The shipping address is always visible and collected first.
 * - The billing address stays hidden until "Bill to a different address?" is ticked.
 * - While billing is hidden, shipping values are mirrored into the (hidden but enabled)
 *   billing fields so they POST correctly, keep required fields non-empty (no hidden
 *   "invalid required control" submit block), and let the MSA Stripe routing guard read
 *   a valid billing_country.
 *
 * Standard DOM APIs are used throughout, except for WooCommerce's checkout events
 * (update_checkout / updated_checkout / country_to_state_changed) which are jQuery custom
 * events on document.body — native listeners can neither receive nor dispatch them, so we
 * use the (WordPress-provided) jQuery instance for those, and only those.
 */

import {onDocumentLoaded} from "@plaudit/frontend-apis";

import $ from 'jquery';

const SUBFIELDS = [
	'first_name',
	'last_name',
	'company',
	'country',
	'address_1',
	'address_2',
	'city',
	'state',
	'postcode',
] as const;

onDocumentLoaded(() => {
	const form = document.querySelector<HTMLFormElement>('form.checkout');
	const toggle = document.getElementById('bill-to-different-address-checkbox');
	if (!(toggle instanceof HTMLInputElement)) {
		return;
	}
	const billing = document.querySelector<HTMLElement>('.woocommerce-billing-fields .billing_address');

	// Only run on the inverted classic checkout (the toggle is present).
	if (!form || !toggle || !billing) {
		return;
	}

	function getField(id: string) {
		const field = document.getElementById(id);
		return field instanceof HTMLSelectElement || field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field : undefined;
	}

	const billingIsSeparate = (): boolean => toggle.checked;

	/** Copy one shipping subfield into its billing counterpart. Returns true if it changed. */
	const mirrorField = (sub: string): boolean => {
		const src = getField('shipping_' + sub);
		const dst = getField('billing_' + sub);
		if (!src || !dst || dst.value === src.value) {
			return false;
		}
		dst.value = src.value;
		return true;
	};

	/**
	 * Mirror all shipping subfields into billing while billing is NOT separate.
	 * When `silent` is false and the country changed, dispatch a `change` event on
	 * #billing_country so WooCommerce rebuilds the billing state field and the Stripe
	 * routing guard re-evaluates the account. `silent` is used inside WC update cycles to
	 * avoid re-triggering work that the cycle already performs.
	 */
	const mirrorAll = (silent = false): void => {
		if (billingIsSeparate()) {
			return;
		}
		let countryChanged = false;
		for (const sub of SUBFIELDS) {
			const changed = mirrorField(sub);
			if (changed && sub === 'country') {
				countryChanged = true;
			}
		}
		if (countryChanged && !silent) {
			// A native, bubbling `change` is caught by WooCommerce's delegated jQuery
			// handlers (wc-country-select, the Stripe country guard) just like a real one.
			getField('billing_country')?.dispatchEvent(new Event('change', {bubbles: true}));
		}
	};

	const applyVisibility = (): void => {
		if (billingIsSeparate()) {
			billing.style.display = '';
		} else {
			mirrorAll();
			billing.style.display = 'none';
		}
	};

	// Initial state: billing collapsed and mirroring shipping.
	applyVisibility();

	toggle.addEventListener('change', () => {
		applyVisibility();
		$(document.body).trigger('update_checkout');
	});

	// Keep billing in sync whenever a shipping field changes (while collapsed). `input` and
	// `change` both bubble, so a single delegated listener on the container is enough.
	const shippingFields = document.querySelector('.woocommerce-shipping-fields');
	if (shippingFields) {
		const onShippingChanged = () => mirrorAll();
		shippingFields.addEventListener('input', onShippingChanged);
		shippingFields.addEventListener('change', onShippingChanged);
	}

	// Re-mirror after WooCommerce re-renders address fields (state/postcode i18n swap,
	// totals refresh). Silent to avoid re-triggering the country change mid-cycle.
	$(document.body).on('updated_checkout country_to_state_changed', () => mirrorAll(true));

	// Final safety net before submit so nothing is stale.
	form.addEventListener('submit', () => mirrorAll(true));
});
